import * as Sentry from "@sentry/react";

import {
    AvailableProductsWACResponse,
    GetAccountOrgsResponse,
    GetAdminhubAccountOrgsResponse
} from "@/libraries/bxp-web-stargate-client/src";

import { SENTRY_PROJECT_NAME } from "../constants";

import { CloudSitesByOrg } from "./types";

import type { FastShiftState } from "./reducer";

const parseBaseUrlFromContinueUrl = (continueUrl: string): string => {
    return `https://${continueUrl.split("continue=https%3A%2F%2F")[1]}` || "";
};

export const transformAvailableProductsToCloudSitesByOrg = (
    availableProducts: AvailableProductsWACResponse
): CloudSitesByOrg[] => {
    const adminProducts = availableProducts.sites.filter(
        (site) => site.adminAccess
    );
    const cloudSitesByOrg: CloudSitesByOrg[] = [];

    adminProducts.forEach((product) => {
        const siteDetails: CloudSitesByOrg["cloudSites"][number] = {
            displayName: product.displayName,
            baseUrl: parseBaseUrlFromContinueUrl(product.url),
            cloudId: product.cloudId || ""
        };

        const org = product.org;
        if (org) {
            const seenOrg = cloudSitesByOrg.find(
                (orgObject) => orgObject.orgId === org.id
            );
            if (seenOrg) {
                seenOrg.cloudSites.push(siteDetails);
            } else {
                cloudSitesByOrg.push({
                    orgId: org.id,
                    orgName: org.displayName,
                    cloudSites: [siteDetails]
                });
            }
        }
    });

    return cloudSitesByOrg;
};

// accountOrgs contains active and inactive orgs (due to a server-side bug)
// adminhubAccountOrgs contains only active orgs, but not whether the user is an org admin
// we can find active orgs while preserving org admin field by returning common items
export const filterForActiveOrgs = (
    accountOrgs: GetAccountOrgsResponse["accountOrgs"],
    adminhubAccountOrgs: GetAdminhubAccountOrgsResponse["accountOrgs"]
): GetAccountOrgsResponse["accountOrgs"] => {
    if (!accountOrgs || !adminhubAccountOrgs) {
        return [];
    }

    const mergedAccountOrgs = accountOrgs.filter((org) => {
        return adminhubAccountOrgs?.find(
            (adminhubOrg) => adminhubOrg.id === org.id
        );
    });

    return mergedAccountOrgs;
};

export const getUserDomain = (state: FastShiftState) => {
    return state.userDetails.data?.userDomain?.domain;
};

export const captureErrorWithContext = ({
    error,
    message,
    userDomain
}: {
    error: string;
    message: string;
    userDomain?: string;
}) => {
    Sentry.captureException(new Error(error), {
        tags: {
            projectName: SENTRY_PROJECT_NAME
        },
        extra: {
            errorMessage: message,
            userDomain: userDomain || ""
        }
    });
};
